import { useEffect, useReducer, useState } from "react";
import { U2000GridStateEnumPaginationDirection, type Pagination, type U2000GridState } from "./U2000GridState";
import u2000GridState, { U2000GridStateEnumAction } from "./U2000GridState";
import type { U2000TableColumn } from "../../components/tables/basic/U2000BasicTableList";
import type { U2000SearchResult } from "../../components/tables/basic/U2000BasicTable";

export const U2000GridStateDeleteAction = {
  RECORD: "RECORD",
  PAGENEXT: "PAGENEXT",
  PAGEPREVIOUS: "PAGEPREVIOUS",
  LIST: "LIST",
} as const;
export type U2000GridStateDeleteAction = (typeof U2000GridStateDeleteAction)[keyof typeof U2000GridStateDeleteAction];

export interface U2000GridStateNextRecordAfterDeleteReturn {
  action: U2000GridStateDeleteAction;
  id: string | null;
  page: number | null;
}

export interface U2000GridStateNextPageAfterDeleteReturn {
  action: U2000GridStateDeleteAction;
  page: number | null;
}

// Interface regroupant les handlers pour le form state
export interface U2000GridStateHandlers<T extends Record<string, any>> {
  // conteins l'ensemble des colonnes du grid
  setColumns: (columns: U2000TableColumn[]) => void;
  getColumns: () => U2000TableColumn[];

  // contient l'ensemble des données du grid
  setData: (rows: T[]) => void;
  setDataById: (id: string, row: T) => void;
  getData: () => T[];
  getCurrentRow: () => T | undefined;
  setCurrentRow: (row: T) => void;
  nextRow: () => number;
  previousRow: () => number;

  // gestion de la pagination et total des lignes
  setPage: (page: number) => void;
  getCurrentPage: () => number;
  setSize: (size: number) => void;
  getSize: () => number;
  getPagination: () => Pagination;
  extractPagination: (page: number) => Pagination;
  setTotalRows: (totalRows: number) => void;
  getTotalRows: () => number;

  // gestion des champs sélectionnés et triés pour la recherche
  setSelectedFields: (name: string[]) => void;
  getSelectedFields: () => string[];
  setSelectedSortedFields: (sortedFields: { name: string; sortOrder?: string }[]) => void;
  getSelectedSortedFields: () => { name: string; sortOrder?: string }[];

  // index de la ligne courante pour navigation en mode forme
  setCurrentIdx: (idx: number) => void;
  getCurrentIdx: () => number;

  // gestion de la sélection des lignes
  selectAllRow: (isChecked: boolean) => void;
  selectRow: (id: string, isChecked: boolean) => void;
  getSelectedRows: () => T[];

  // tools
  initGridAfterSearch: (searchResult: U2000SearchResult<T>, direction: U2000GridStateEnumPaginationDirection) => number;
  getNextPageAfterDelete: (page: number, totalRows: number) => U2000GridStateNextPageAfterDeleteReturn;
  getNextRecordAfterDelete: (currentId: number, data: T[], totalRows: number) => U2000GridStateNextRecordAfterDeleteReturn;
  getNextRecordAfterDeleteOnGridWithoutPagination: (currentId: number, data: T[], totalRows: number) => U2000GridStateNextRecordAfterDeleteReturn;
  setErrors: (errors: string[]) => void;
  getErrors: () => string[];
  isShowErrorsAlert: () => boolean;
  onHideErrorsAlert: () => void;
}

export interface U2000GridStateHandlersParams {
  nbRecordPerPage?: number;
  gridState?: U2000GridState;
}

export interface U2000GridStateHandlersReturn<T extends Record<string, any>> {
  setColumns: (columns: U2000TableColumn[]) => void;
  getColumns: () => U2000TableColumn[];
  setData: (rows: T[]) => void;
  setDataById: (id: string, row: T) => void;
  getData: () => T[];
  getCurrentRow: () => T | undefined;
  setCurrentRow: (row: T) => void;
  nextRow: () => number;
  previousRow: () => number;
  setPage: (page: number) => void;
  getCurrentPage: () => number;
  extractPagination: (page: number) => Pagination;
  setSize: (size: number) => void;
  getSize: () => number;
  getPagination: () => Pagination;
  setTotalRows: (totalRows: number) => void;
  getTotalRows: () => number;
  setSelectedFields: (name: string[]) => void;
  getSelectedFields: () => string[];
  setSelectedSortedFields: (sortedFields: { name: string; sortOrder?: string }[]) => void;
  getSelectedSortedFields: () => { name: string; sortOrder?: string }[];
  setCurrentIdx: (idx: number) => void;
  getCurrentIdx: () => number;
  selectAllRow: (isChecked: boolean) => void;
  selectRow: (id: string, isChecked: boolean) => void;
  getSelectedRows: () => T[];

  initGridAfterSearch: (searchResult: U2000SearchResult<T>, direction: U2000GridStateEnumPaginationDirection) => number;
  getNextPageAfterDelete: (page: number, totalRows: number) => U2000GridStateNextPageAfterDeleteReturn;
  getNextRecordAfterDelete: (currentId: number, data: T[], totalRows: number) => U2000GridStateNextRecordAfterDeleteReturn;
  getNextRecordAfterDeleteOnGridWithoutPagination: (currentId: number, data: T[], totalRows: number) => U2000GridStateNextRecordAfterDeleteReturn;
  setErrors: (errors: string[]) => void;
  getErrors: () => string[];
  isShowErrorsAlert: () => boolean;
  onHideErrorsAlert: () => void;
}

export const u2000IntGridState: U2000GridState = {
  columns: [],
  data: [],
  selectedFields: [],
  selectedSortedFields: [],

  pagination: { page: 1, size: 1 },
  totalRows: 0,
  currentIdx: 0,
  errors: [],
};

export function useU2000GridStateHandlers<T extends Record<string, any>>(params: U2000GridStateHandlersParams = {}): U2000GridStateHandlersReturn<T> {
  const { nbRecordPerPage } = params;
  const [gridState, gridStateDispatch] = useReducer<U2000GridState, any>(u2000GridState, u2000IntGridState);
  const [isVisibleGridBoxError, setIsVisibleGridBoxError] = useState<boolean>(false);

  const setColumns = (columns: U2000TableColumn[]) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETCOLUMNS, data: { columns } });
  };

  useEffect(() => {
    setSize(nbRecordPerPage ?? 10);
  }, []);

  const getColumns = () => {
    return gridState.columns;
  };

  const getData = () => {
    return gridState.data as T[];
  };

  const setData = (rows: T[]) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETDATA, data: { rows } });
  };

  const setDataById = (id: string, row: T) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETDATABYID, data: { id, row } });
  };

  const getCurrentRow = () => {
    return gridState.data[gridState.currentIdx] as T | undefined;
  };

  const setCurrentRow = (row: T) => {
    const updatedData = gridState.data.map((r, index) => {
      if (index === gridState.currentIdx) {
        return row;
      }
      return r;
    });
    setData(updatedData);
  };

  const nextRow = () => {
    if (gridState.currentIdx < gridState.data.length - 1) {
      setCurrentIdx(gridState.currentIdx + 1);
      return gridState.currentIdx + 1;
    }
    return gridState.currentIdx;
  };

  const previousRow = () => {
    if (gridState.currentIdx > 0) {
      setCurrentIdx(gridState.currentIdx - 1);
      return gridState.currentIdx - 1;
    }
    return gridState.currentIdx;
  };

  const setPage = (page: number) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETPAGE, data: { page } });
  };

  const getCurrentPage = () => {
    return gridState.pagination.page;
  };

  const extractPagination = (page: number) => {
    setPage(page);
    return { ...gridState.pagination, page };
  };

  const setSize = (size: number) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETSIZE, data: { size } });
  };

  const getSize = () => {
    return gridState.pagination.size;
  };

  const getPagination = () => {
    return gridState.pagination;
  };

  const setTotalRows = (totalRows: number) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETTOTALROWS, data: { totalRows } });
  };

  const getTotalRows = () => {
    return gridState.totalRows;
  };

  const setSelectedFields = (name: string[]) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETSELECTEDFIELDS, data: { name } });
  };

  const getSelectedFields = () => {
    return gridState.selectedFields as string[];
  };

  const setSelectedSortedFields = (sortedFields: { name: string; sortOrder?: string }[]) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETSELECTEDSORTEDFIELDS, data: { sortedFields } });
  };

  const getSelectedSortedFields = () => {
    return gridState.selectedSortedFields as { name: string; sortOrder?: string }[];
  };

  const setCurrentIdx = (idx: number) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.SETCURRENTIDX, data: { idx } });
  };

  const getCurrentIdx = () => {
    return gridState.currentIdx;
  };

  const selectAllRow = (isChecked: boolean) => {
    const updatedData = gridState.data.map((row) => {
      return { ...row, idChecked: isChecked };
    });
    setData(updatedData as T[]);
  };

  const selectRow = (id: string, isChecked: boolean) => {
    const updatedData = gridState.data.map((row) => {
      if (row.id === id) {
        return { ...row, idChecked: isChecked };
      }
      return row;
    });
    setData(updatedData as T[]);
  };

  const getSelectedRows = (): T[] => {
    return gridState.data.filter((row) => row.idChecked === true) as T[];
  };

  const initGridAfterSearch = (searchResult: U2000SearchResult<T>, direction: U2000GridStateEnumPaginationDirection) => {
    let idx = 0;
    if (direction === U2000GridStateEnumPaginationDirection.NEXTPAGE) {
      idx = 0;
    } else if (direction === U2000GridStateEnumPaginationDirection.PREVIOUSPAGE) {
      idx = searchResult.data.length - 1;
    }

    setSelectedSortedFields(searchResult.input.sortedFields);
    setSelectedFields(searchResult.input.fields.map((field) => field.name));
    setPage(searchResult.pagination.page);
    setTotalRows(searchResult.nbRecord);
    setData(searchResult.data);
    setCurrentIdx(idx);

    return idx;
  };

  const getNextPageAfterDelete = (page: number, totalRows: number): U2000GridStateNextPageAfterDeleteReturn => {
    const totalPage = Math.ceil(totalRows / gridState.pagination.size);

    if (totalRows === 0) {
      return { action: U2000GridStateDeleteAction.PAGENEXT, page: 1 }; // Aucun enregistrement restant
    }
    if (page <= totalPage) {
      return { action: U2000GridStateDeleteAction.PAGENEXT, page: page }; // Aller à la page suivante
    } else {
      return { action: U2000GridStateDeleteAction.PAGENEXT, page: totalPage }; // Aller à la page suivante
    }
  };

  const getNextRecordAfterDelete = (currentIdx: number, data: T[], totalRows: number): U2000GridStateNextRecordAfterDeleteReturn => {
    const dataLength = data.length;
    const totalPage = Math.ceil(totalRows / gridState.pagination.size);
    const currentPage = gridState.pagination.page;
    const currentRow = currentIdx + 1 + gridState.pagination.size * (currentPage - 1);

    if (totalRows === 0) {
      return { action: U2000GridStateDeleteAction.LIST, id: null, page: null }; // Aucun enregistrement restant
    }

    if (currentRow <= totalRows) {
      if (currentIdx < dataLength) {
        return { action: U2000GridStateDeleteAction.RECORD, id: data[currentIdx].id as string, page: null }; // Aller au prochain enregistrement
      } else if (currentPage <= totalPage) {
        return { action: U2000GridStateDeleteAction.PAGENEXT, id: null, page: currentPage }; // Aller à la page suivante
      }
    } else {
      if (dataLength > 0) {
        const newIdx = dataLength - 1;
        setCurrentIdx(newIdx);
        return { action: U2000GridStateDeleteAction.RECORD, id: data[newIdx].id as string, page: null }; // Aller au prochain enregistrement
      } else if (totalPage > 0) {
        return { action: U2000GridStateDeleteAction.PAGENEXT, id: null, page: totalPage }; // Aller à la page précédente
      }
    }
    return { action: U2000GridStateDeleteAction.LIST, id: null, page: null }; // Aucun enregistrement disponible
  };

  const getNextRecordAfterDeleteOnGridWithoutPagination = (currentIdx: number, data: T[], totalRows: number): U2000GridStateNextRecordAfterDeleteReturn => {
    const dataLength = data.length;

    if (totalRows === 0) {
      return { action: U2000GridStateDeleteAction.LIST, id: null, page: null }; // Aucun enregistrement restant
    }

    if (currentIdx < dataLength) {
      return { action: U2000GridStateDeleteAction.RECORD, id: data[currentIdx].id as string, page: null }; // Aller au prochain enregistrement
    } else if (currentIdx - 1 >= 0) {
      setCurrentIdx(currentIdx - 1);
      return { action: U2000GridStateDeleteAction.RECORD, id: data[currentIdx - 1].id as string, page: null }; // Aller à l'enregistrement précédent
    }
    return { action: U2000GridStateDeleteAction.LIST, id: null, page: null }; // Aucun enregistrement disponible
  };

  const setErrors = (errors: string[]) => {
    gridStateDispatch({ type: U2000GridStateEnumAction.GRIDERRORS, data: { errors } });
    if (errors.length > 0) setIsVisibleGridBoxError(true);
    else setIsVisibleGridBoxError(false);
  };

  const getErrors = () => {
    return gridState.errors;
  };

  const isShowErrorsAlert = () => {
    return isVisibleGridBoxError;
  };

  const onHideErrorsAlert = () => {
    setIsVisibleGridBoxError(false);
  };

  return {
    setColumns,
    getColumns,
    setData,
    setDataById,
    getData,
    getCurrentRow,
    setCurrentRow,
    nextRow,
    previousRow,
    setPage,
    getCurrentPage,
    extractPagination,
    setSize,
    getSize,
    getPagination,
    setTotalRows,
    getTotalRows,
    setSelectedFields,
    getSelectedFields,
    setSelectedSortedFields,
    getSelectedSortedFields,
    setCurrentIdx,
    getCurrentIdx,
    selectAllRow,
    selectRow,
    getSelectedRows,
    initGridAfterSearch,
    getNextPageAfterDelete,
    getNextRecordAfterDelete,
    getNextRecordAfterDeleteOnGridWithoutPagination,
    setErrors,
    getErrors,
    isShowErrorsAlert,
    onHideErrorsAlert,
  };
}
